import { useCallback, useEffect, useMemo } from 'react';
import { OverrideType } from '@uikit/FormItems/constants';
import {
  LOCATION_DEFAULT_VALUES,
  LOCATION_TITLES,
} from '@src/modals/LocationModal/constants';
import { removeCounty } from '@src/utils';
import { toStateAbbreviation } from '@component/Locations/constants';

export const useOverRide = ({
  override,
  getValues,
  reset,
}: {
  override: any;
  getValues: any;
  reset: any;
}) => {
  const isOverrideObject = useCallback(
    (override: boolean | OverrideType): override is OverrideType => {
      return typeof override !== 'boolean';
    },
    []
  );
  const overrideData = useMemo(() => {
    if (isOverrideObject(override)) {
      const {
        address1,
        address2,
        state,
        businessName,
        latitude,
        longitude,
        phoneNumber,
        phoneType,
        id,
        type,
        zipCode,
        city,
        country,
        verified,
        verifiedAddress,
        apiFrom,
        googlePlaceId,
      } = override;

      return {
        address1,
        address2,
        state: {
          id: null,
          label: toStateAbbreviation(state),
          value: toStateAbbreviation(state),
        },
        businessName,
        latitude,
        longitude,
        phoneNumber,
        phoneType,
        id,
        type,
        zipCode,
        city,
        country:
          typeof country === 'string'
            ? { id: null, label: removeCounty(country), value: country }
            : country
              ? {
                  ...country,
                  label: removeCounty(String(country?.label ?? country?.value)),
                }
              : country,
        verified,
        verifiedAddress: verifiedAddress ?? verified,
        apiFrom,
        googlePlaceId,
      };
    }

    return null;
  }, [override, isOverrideObject, getValues]);

  const locationId = useMemo(() => {
    if (!overrideData || !overrideData.id) return '';
    return `- ${overrideData.id}`;
  }, [overrideData]);

  useEffect(() => {
    if (!overrideData) {
      reset({ ...LOCATION_DEFAULT_VALUES });
    } else {
      reset(overrideData);
    }
  }, [overrideData, reset]);

  const exactLocationTitle = useMemo(() => {
    if (!overrideData) return 'Manually Entered Location';
    return LOCATION_TITLES[overrideData.apiFrom as 'storage' | 'google'];
  }, [overrideData]);

  return { overrideData, locationId, exactLocationTitle, isOverrideObject };
};
